/**
 * The user's own day shape — where their day ends.
 *
 * SC-35 forbids assuming a day shape (midnight-to-midnight, 9-to-5, a 21:00
 * "evening"). The rest-of-today timeline runs from now to the user's SLEEP
 * ANCHOR, and that anchor is a preference the user set themselves. When it
 * cannot be read, the answer is `null` — the timeline then draws no end marker
 * rather than inventing one.
 *
 * THE SUBSTITUTED-DEFAULT TRAP
 * ----------------------------
 * `GET /api/scheduling-preferences` does not return the profile: it returns the
 * profile with server-side fallbacks already substituted
 * (`scheduling_preferences_routes.py:196-197` — `profile.wake_time or "07:00"`,
 * `profile.sleep_time or "23:00"`). A user who has never opened the preferences
 * screen therefore gets a fully-populated, entirely invented day shape, and
 * this service used to hand it on as if the user had chosen it. Every timeline
 * drew to 23:00 and printed "winds down at 23:00" over somebody's night shift.
 *
 * A substituted default is NOT knowledge, so it reads as `null` here. The cost
 * is narrow and deliberately one-directional: a user whose real sleep time is
 * exactly the substituted value loses an end marker they could have had, which
 * is the SC-35-correct way to be wrong — draw nothing rather than assert a day
 * shape nobody chose.
 *
 * The right long-term fix is on the server: say which fields were defaulted.
 * `defaulted()` below already honors that answer (`defaults_applied`) the
 * moment the endpoint starts sending it, and only falls back to comparing
 * against the substituted constants until then.
 *
 * Read-only and cheap (one profile document). Nothing here writes.
 */

export interface DayShape {
  /** "HH:MM" the user set as their wake time, or null when it is not known. */
  wakeTime: string | null
  /** "HH:MM" the user set as their wind-down, or null when it is not known. */
  sleepTime: string | null
}

/**
 * What the endpoint substitutes when the profile carries nothing of its own.
 * Mirrors scheduling_preferences_routes.get_preferences; if that route's
 * fallbacks change, change these with them.
 */
export const ENDPOINT_DEFAULTS: Readonly<Record<'wake_time' | 'sleep_time', string>> = {
  wake_time: '07:00',
  sleep_time: '23:00',
}

const HHMM = /^\d{1,2}:\d{2}$/

function hhmm(value: unknown): string | null {
  return typeof value === 'string' && HHMM.test(value.trim()) ? value.trim() : null
}

/** True when this field's value came from the endpoint, not from the user. */
function defaulted(body: unknown, field: 'wake_time' | 'sleep_time', value: string): boolean {
  // Preferred: the server says so outright. (Not sent today; honored on arrival.)
  const applied = (body as { defaults_applied?: unknown } | null)?.defaults_applied
  if (Array.isArray(applied)) return applied.includes(field)
  // Fallback: the value is character-identical to what the route substitutes.
  return value === ENDPOINT_DEFAULTS[field]
}

/** The stored preference, or null when the endpoint answered for the user. */
function stored(body: unknown, field: 'wake_time' | 'sleep_time', raw: unknown): string | null {
  const value = hhmm(raw)
  if (value === null) return null
  return defaulted(body, field, value) ? null : value
}

export async function fetchDayShape(): Promise<DayShape> {
  const unknown: DayShape = { wakeTime: null, sleepTime: null }
  try {
    const res = await fetch('/api/scheduling-preferences', { credentials: 'include' })
    if (!res.ok) return unknown
    const body = await res.json()
    const prefs = body?.preferences ?? {}
    return {
      wakeTime: stored(body, 'wake_time', prefs.wake_time),
      sleepTime: stored(body, 'sleep_time', prefs.sleep_time),
    }
  } catch {
    return unknown
  }
}
